import { cloneDeep } from 'lodash';
import { FormFieldType, MatrixColumnType, type FormField } from '@/models/form';
import type { FileUploads } from '../files/fileUploads';
import { syncSlot } from './agreedFileIds';
import { readOnlyOf, type FieldPatch } from './fieldItem';
import { matrixDataCalculations } from './MatrixHelper';

/**
 * Runs `syncSlot` over every file field and matrix file cell in `fields`, as each item would on its next mount, and
 * writes what moved through `change`. The field list is virtualized, so a file that stored after its item scrolled out
 * of view is in no answer until this runs; Save runs it once the uploads have settled (ENG-9092). A file field writes
 * `fileIds`, and a file cell writes `fileIds` and `textValue` into a copy of its matrix's rows with the formula columns
 * worked out again, as the matrix does for every change the reader makes. A field its effects or a calculation make
 * read-only takes nothing, as its item would take nothing.
 */
export const syncFileAnswers = (
  uploads: FileUploads,
  fields: FormField[],
  change: (identifier: string, patch: FieldPatch) => void
) => {
  for (const field of fields) {
    if (readOnlyOf(field, false) !== null) {
      continue;
    }
    if (field.type === FormFieldType.FILE) {
      syncSlot(uploads, { fieldId: field.identifier }, field.fileIds,
        fileIds => change(field.identifier, { fileIds }));
    }
    if (field.type === FormFieldType.MATRIX && field.matrixTableRowValues) {
      const rows = cloneDeep(field.matrixTableRowValues);
      let moved = false;
      for (const row of rows) {
        for (const cell of row.columnValues ?? []) {
          if (cell.type === MatrixColumnType.FILE) {
            syncSlot(uploads, { fieldId: field.identifier, rowId: row.rowId, columnId: `${cell.id}` }, cell.fileIds,
              fileIds => {
                Object.assign(cell, { fileIds, textValue: JSON.stringify(fileIds) });
                moved = true;
              });
          }
        }
      }
      if (moved) {
        change(field.identifier, {
          matrixTableRowValues: matrixDataCalculations(field.options.columns ?? [], rows),
          hasExistingMatrixValue: true
        });
      }
    }
  }
};
